import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge, ACCOUNT_LABEL } from "@/pages/admin/AdminCompanies";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Undo2, Trash2 } from "lucide-react";

/**
 * Arşivlenmiş firmaların listesi.  Buradan admin firmayı Geri Yükle
 * (Passive'e döndürür) veya Silinmişler'e Taşı (soft-delete) yapabilir.
 * Firma verileri korunur; hiçbir operasyonel veri silinmez.
 */
export default function AdminArchive() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { action: 'restore'|'soft-delete', row }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/companies", {
        params: { filter: "archived" },
      });
      setRows(data || []);
    } catch {
      toast.error("Arşiv listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!confirm) return;
    const { action, row } = confirm;
    setBusy(true);
    try {
      const path = action === "restore" ? "restore" : "soft-delete";
      await api.patch(`/admin/companies/${row.id}/${path}`, {});
      toast.success(
        action === "restore"
          ? "Firma pasif duruma geri yüklendi."
          : "Firma Silinmişler'e taşındı.",
      );
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız oldu.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-archive-page">
      <PageHeader
        title="Arşiv"
        subtitle="Arşivdeki firmalar. Firma verileri korunur; kalıcı imha edilene kadar geri yüklenebilir."
      />

      <SectionCard>
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="Arşivde firma bulunmuyor" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Firma</th>
                  <th className="px-5 py-3 font-semibold">Yetkili</th>
                  <th className="px-5 py-3 font-semibold">E-posta</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Pasife Alınma</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Arşive Alınma</th>
                  <th className="px-5 py-3 font-semibold">Durum</th>
                  <th className="px-5 py-3 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50/60"
                    data-testid={`admin-archive-row-${r.id}`}
                  >
                    <td
                      className="px-5 py-3 cursor-pointer"
                      onClick={() => navigate(`/admin/companies/${r.id}`)}
                    >
                      <div className="font-medium text-slate-900">{r.name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.owner?.username || ""}</div>
                    </td>
                    <td className="px-5 py-3">{r.owner?.full_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-700 truncate max-w-[220px]">
                      {r.owner?.email || "—"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                      {r.inactive_at ? fmtDateTime(r.inactive_at) : "—"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                      {r.archived_at ? fmtDateTime(r.archived_at) : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={ACCOUNT_LABEL[r.account_status]} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-2 rounded-lg"
                        onClick={() => setConfirm({ action: "restore", row: r })}
                        data-testid={`admin-archive-restore-${r.id}`}
                      >
                        <Undo2 className="w-4 h-4 mr-1" /> Geri Yükle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => setConfirm({ action: "soft-delete", row: r })}
                        data-testid={`admin-archive-soft-delete-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Silinmişler&apos;e Taşı
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="admin-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "restore"
                ? "Firmayı geri yükle"
                : "Firmayı Silinmişler'e taşı"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "restore" ? (
                <>
                  <span className="font-medium">{confirm?.row?.name}</span> firması arşivden çıkarılıp <b>Pasif</b> duruma alınacak. Firma verileri korunur; abonelik otomatik olarak aktifleştirilmez.
                </>
              ) : (
                <>
                  <span className="font-medium">{confirm?.row?.name}</span> firması <b>Silinmişler</b> bölümüne taşınacak. Kalıcı imha yapılmaz; veriler korunur ve geri yüklenebilir.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-archive-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runAction}
              disabled={busy}
              className={
                confirm?.action === "restore"
                  ? "bg-slate-900 hover:bg-slate-800"
                  : "bg-rose-600 hover:bg-rose-700"
              }
              data-testid="admin-archive-confirm-ok"
            >
              {busy ? "İşleniyor…" : confirm?.action === "restore" ? "Geri Yükle" : "Silinmişler'e Taşı"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
